import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, History, Loader2 } from 'lucide-react';
import Modal from './Modal';
import Alert from './Alert';
import {
    documentOperationScopeKey,
    getCachedCaseZip,
    getDocumentOperationDetails,
    listDocumentOperations,
    resumeDocumentOperation,
    undoDocumentOperation,
} from '../services/documentOperationsApi';
import { DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT } from '../utils/documentOperationUndo';

const OPERATION_LABELS = Object.freeze({
    upload: 'Upload',
    copy: 'Copy',
    move: 'Move',
    delete: 'Delete',
});
const STATUS_LABELS = Object.freeze({
    available: 'Completed',
    complete: 'Completed',
    completed: 'Completed',
    pending: 'In progress',
    running: 'In progress',
    needs_review: 'Needs review',
    undoing: 'Undo in progress',
    undone: 'Undone',
    failed: 'Failed',
    unconfirmed: 'Needs confirmation',
    expired: 'Expired',
});

const LOCAL_UPLOAD_STATUSES = new Set(['failed', 'unconfirmed']);

const publicDocumentPath = (value) => {
    const normalized = String(value ?? '')
        .replace(/\\/g, '/')
        .replace(/^cases\/\d+\//i, '')
        .replace(/\{\{Name\}\}/gi, 'Case documents')
        .trim();
    return normalized || 'Location unavailable';
};

const normalizeLocalUploadGroups = (groups) => (Array.isArray(groups) ? groups : [])
    .map((group) => {
        const id = String(group?.id ?? '').trim();
        const status = String(group?.status ?? '').trim().toLowerCase();
        if (!id || !LOCAL_UPLOAD_STATUSES.has(status)) return null;
        const items = (Array.isArray(group?.items) ? group.items : [])
            .map((item, index) => {
                const name = String(item?.name ?? '').trim();
                const path = publicDocumentPath(item?.path);
                if (!name && path === 'Location unavailable') return null;
                return {
                    id: String(item?.id ?? index),
                    name: name || path.split('/').pop() || 'Unnamed file',
                    path,
                    status: status === 'failed' ? 'Failed' : 'Needs confirmation',
                };
            })
            .filter(Boolean);
        return {
            id,
            status,
            destinationLabel: publicDocumentPath(group?.destinationLabel),
            items,
            objectCount: items.length,
            canReview: status === 'failed' && group?.canReview === true,
        };
    })
    .filter(Boolean);

const ITEM_STATUS_LABELS = Object.freeze({
    available: 'Completed',
    complete: 'Completed',
    completed: 'Completed',
    copied: 'Completed',
    deleted: 'Completed',
    moved: 'Completed',
    uploaded: 'Completed',
    failed: 'Failed',
    missing: 'Not found',
    pending: 'In progress',
    restored: 'Restored',
    running: 'In progress',
    skipped: 'Skipped',
    unconfirmed: 'Needs confirmation',
});

const normalizeOperationDetails = (details) => {
    if (!details || typeof details !== 'object' || Array.isArray(details)) {
        throw new Error('The item details for this action are unavailable.');
    }
    const items = (Array.isArray(details.items) ? details.items : [])
        .map((item, index) => {
            if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
            const name = String(item.name ?? '').trim();
            const path = publicDocumentPath(item.path);
            if (!name && path === 'Location unavailable') return null;
            const receivedStatus = String(item.status ?? '').trim().toLowerCase();
            return {
                id: String(item.id ?? index),
                name: name || path.split('/').pop() || 'Unnamed file',
                path,
                status: ITEM_STATUS_LABELS[receivedStatus] || 'Recorded',
            };
        })
        .filter(Boolean);
    const receivedTotal = Number(details.total);
    const total = Number.isSafeInteger(receivedTotal) && receivedTotal >= items.length
        ? receivedTotal
        : items.length;
    return {
        items,
        total,
        available: details.available !== false,
        truncated: details.available !== false && details.truncated === true,
    };
};

const operationTime = (value) => {
    if (!value) return 'Time unavailable';
    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return 'Time unavailable';
    return parsed.toLocaleString([], {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });
};

const fileSizeLabel = (value) => {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes < 0) return 'Size unavailable';
    if (bytes < 1024) return `${Math.round(bytes)} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let amount = bytes / 1024;
    let unitIndex = 0;
    while (amount >= 1024 && unitIndex < units.length - 1) {
        amount /= 1024;
        unitIndex += 1;
    }
    return `${amount >= 10 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unitIndex]}`;
};

export const DocumentRecentActions = ({
    caseId,
    userId,
    onChanged,
    localUploadGroups = [],
    onReviewLocalUploadGroup,
    onDismissLocalUploadGroup,
}) => {
    const scopeKey = documentOperationScopeKey({ caseId, userId });
    return (
        <ScopedDocumentRecentActions
            key={scopeKey}
            caseId={caseId}
            userId={userId}
            onChanged={onChanged}
            localUploadGroups={localUploadGroups}
            onReviewLocalUploadGroup={onReviewLocalUploadGroup}
            onDismissLocalUploadGroup={onDismissLocalUploadGroup}
        />
    );
};

const ScopedDocumentRecentActions = ({
    caseId,
    userId,
    onChanged,
    localUploadGroups,
    onReviewLocalUploadGroup,
    onDismissLocalUploadGroup,
}) => {
    const scope = useMemo(() => ({ caseId: String(caseId ?? ''), userId: String(userId ?? '') }), [caseId, userId]);
    const scopeKey = documentOperationScopeKey(scope);
    const scopeLifetime = useMemo(() => ({}), [scopeKey]);
    const currentLifetimeRef = useRef(scopeLifetime);
    currentLifetimeRef.current = scopeLifetime;
    const mountedRef = useRef(false);
    const historyControllerRef = useRef(null);
    const undoControllerRef = useRef(null);
    const detailsControllerRef = useRef(null);
    const cachedCaseZipControllerRef = useRef(null);
    const historyGenerationRef = useRef(0);
    const undoInFlightRef = useRef(null);
    const triggerRef = useRef(null);
    const dialogRef = useRef(null);
    const [operations, setOperations] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyReceiptId, setBusyReceiptId] = useState(null);
    const [detailTarget, setDetailTarget] = useState(null);
    const [operationDetails, setOperationDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsLoadingMore, setDetailsLoadingMore] = useState(false);
    const [detailsError, setDetailsError] = useState('');
    const [cachedCaseZip, setCachedCaseZip] = useState(null);
    const [cachedCaseZipLoading, setCachedCaseZipLoading] = useState(false);
    const normalizedLocalUploadGroups = useMemo(
        () => normalizeLocalUploadGroups(localUploadGroups),
        [localUploadGroups]
    );
    const caseZipVisible = ['building', 'ready'].includes(cachedCaseZip?.status);
    const actionCount = operations.length + normalizedLocalUploadGroups.length + (caseZipVisible ? 1 : 0);
    const validScope = /^\d+$/.test(scope.caseId) && /^\d+$/.test(scope.userId);

    const isCurrent = useCallback((capturedLifetime) => (
        mountedRef.current && currentLifetimeRef.current === capturedLifetime
    ), []);

    const load = useCallback(async () => {
        if (!validScope || !isCurrent(scopeLifetime)) return;
        historyControllerRef.current?.abort();
        const controller = new AbortController();
        historyControllerRef.current = controller;
        const generation = ++historyGenerationRef.current;
        const current = () => !controller.signal.aborted
            && isCurrent(scopeLifetime)
            && generation === historyGenerationRef.current;

        setLoading(true);
        setError('');
        try {
            const rows = await listDocumentOperations(scope, { signal: controller.signal });
            if (!current()) return;
            setOperations(rows);
        } catch (requestError) {
            if (!current() || requestError?.name === 'AbortError') return;
            // A denial or uncertain response must not leave previously visible
            // rows actionable under the current identity.
            setOperations([]);
            setError(requestError?.message || 'Recent document actions are unavailable.');
        } finally {
            if (current()) setLoading(false);
        }
    }, [isCurrent, scope, scopeLifetime, validScope]);

    const loadCachedCaseZip = useCallback(async () => {
        if (!validScope || !isCurrent(scopeLifetime)) return;
        cachedCaseZipControllerRef.current?.abort();
        const controller = new AbortController();
        cachedCaseZipControllerRef.current = controller;
        const current = () => !controller.signal.aborted
            && cachedCaseZipControllerRef.current === controller
            && isCurrent(scopeLifetime);

        setCachedCaseZipLoading(true);
        try {
            const result = await getCachedCaseZip(scope, { signal: controller.signal });
            if (!current()) return;
            setCachedCaseZip(result);
        } catch (requestError) {
            if (!current() || requestError?.name === 'AbortError') return;
            // Cache readiness is optional. A denial, malformed response, or
            // missing archive must never leave a stale download action visible.
            setCachedCaseZip(null);
        } finally {
            if (current()) setCachedCaseZipLoading(false);
        }
    }, [isCurrent, scope, scopeLifetime, validScope]);

    useEffect(() => {
        if (!open || cachedCaseZip?.status !== 'building') return undefined;
        const timer = window.setTimeout(() => loadCachedCaseZip(), 5000);
        return () => window.clearTimeout(timer);
    }, [cachedCaseZip, loadCachedCaseZip, open]);

    useEffect(() => {
        mountedRef.current = true;
        const handleReceipt = (event) => {
            const receipt = event?.detail;
            if (!receipt?.receiptId
                || String(receipt.caseId ?? '') !== scope.caseId
                || (receipt.userId != null && String(receipt.userId) !== scope.userId)
                || !isCurrent(scopeLifetime)) return;
            // Never trust the event as history. It is only a prompt to fetch the
            // authenticated current-user list from the server.
            load();
        };
        window.addEventListener(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, handleReceipt);
        // Load on entry so interrupted work is visible from the toolbar badge
        // without requiring the user to discover and open the dialog first.
        load();
        return () => {
            mountedRef.current = false;
            historyGenerationRef.current += 1;
            historyControllerRef.current?.abort();
            undoControllerRef.current?.abort();
            detailsControllerRef.current?.abort();
            cachedCaseZipControllerRef.current?.abort();
            undoInFlightRef.current = null;
            window.removeEventListener(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, handleReceipt);
        };
    }, [isCurrent, load, scope.caseId, scope.userId, scopeLifetime]);

    const resetDetails = useCallback(() => {
        detailsControllerRef.current?.abort();
        detailsControllerRef.current = null;
        setDetailTarget(null);
        setOperationDetails(null);
        setDetailsLoading(false);
        setDetailsLoadingMore(false);
        setDetailsError('');
    }, []);

    const close = ({ restoreFocus = true } = {}) => {
        cachedCaseZipControllerRef.current?.abort();
        cachedCaseZipControllerRef.current = null;
        setCachedCaseZip(null);
        setCachedCaseZipLoading(false);
        resetDetails();
        setOpen(false);
        if (restoreFocus) window.setTimeout(() => triggerRef.current?.focus(), 0);
    };

    useEffect(() => {
        if (!detailTarget) return;
        const targetExists = detailTarget.kind === 'local'
            ? normalizedLocalUploadGroups.some(group => group.id === detailTarget.id)
            : operations.some(operation => operation.receiptId === detailTarget.id);
        if (!targetExists) {
            resetDetails();
        }
    }, [detailTarget, normalizedLocalUploadGroups, operations, resetDetails]);

    useEffect(() => {
        if (!open) return undefined;
        const focusable = dialogRef.current?.querySelector('button:not(:disabled)');
        focusable?.focus();
        return undefined;
    }, [open, loading, busyReceiptId, detailTarget]);

    const handleDialogKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key !== 'Tab') return;
        const buttons = Array.from(
            dialogRef.current?.querySelectorAll('button:not(:disabled)') || []
        );
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!first) {
            event.preventDefault();
            return;
        }
        if (event.shiftKey
            && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const undo = async (operation) => {
        if (!operation?.canUndo || undoInFlightRef.current || loading
            || !operations.some(item => item.receiptId === operation.receiptId)) return;
        const controller = new AbortController();
        undoControllerRef.current = controller;
        undoInFlightRef.current = operation.receiptId;
        setBusyReceiptId(operation.receiptId);
        setError('');
        const capturedLifetime = scopeLifetime;
        try {
            const result = await undoDocumentOperation(scope, operation.receiptId, { signal: controller.signal });
            if (!isCurrent(capturedLifetime) || controller.signal.aborted) return;
            // Disable the committed inverse immediately so a slow listing
            // refresh cannot allow a second click.
            setOperations(current => current.map(item => (
                item.receiptId === operation.receiptId
                    ? { ...item, canUndo: false, status: 'undone' }
                    : item
            )));
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) await load();
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) {
                // Receipt state is authoritative and refreshes first. Folder
                // and tree reconciliation is best effort and must not keep the
                // operation controls globally busy if a view request stalls.
                Promise.resolve()
                    .then(() => onChanged?.(result))
                    .catch(() => {});
            }
        } catch (requestError) {
            if (isCurrent(capturedLifetime) && requestError?.name !== 'AbortError') {
                const message = requestError?.message || 'CaseDB could not safely undo that action.';
                // Revalidate after an uncertain inverse instead of leaving a
                // possibly stale Undo control active.
                await load();
                if (isCurrent(capturedLifetime)) setError(message);
            }
        } finally {
            if (isCurrent(capturedLifetime) && undoInFlightRef.current === operation.receiptId) {
                undoInFlightRef.current = null;
                setBusyReceiptId(null);
            }
        }
    };

    const resume = async (operation) => {
        if (!operation?.canResume || undoInFlightRef.current || loading
            || !operations.some(item => item.receiptId === operation.receiptId)) return;
        const controller = new AbortController();
        undoControllerRef.current = controller;
        undoInFlightRef.current = operation.receiptId;
        setBusyReceiptId(operation.receiptId);
        setError('');
        const capturedLifetime = scopeLifetime;
        try {
            // The server owns the recovery direction. A needs-review receipt
            // can represent either an interrupted delete or an interrupted
            // undo, so the browser must never guess from its public status.
            const result = await resumeDocumentOperation(
                scope,
                operation.receiptId,
                { signal: controller.signal }
            );
            if (!isCurrent(capturedLifetime) || controller.signal.aborted) return;
            // Remove the action immediately. The server refresh below decides
            // whether it is now available, complete, or still needs review.
            setOperations(current => current.map(item => (
                item.receiptId === operation.receiptId
                    ? { ...item, canResume: false, status: 'running' }
                    : item
            )));
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) await load();
            if (isCurrent(capturedLifetime) && !controller.signal.aborted) {
                Promise.resolve()
                    .then(() => onChanged?.(result))
                    .catch(() => {});
            }
        } catch (requestError) {
            if (isCurrent(capturedLifetime) && requestError?.name !== 'AbortError') {
                const message = requestError?.message || 'CaseDB could not safely resume that action.';
                await load();
                if (isCurrent(capturedLifetime)) setError(message);
            }
        } finally {
            if (isCurrent(capturedLifetime) && undoInFlightRef.current === operation.receiptId) {
                undoInFlightRef.current = null;
                setBusyReceiptId(null);
            }
        }
    };

    const toggleServerDetails = async (operation) => {
        if (!operation?.receiptId) return;
        detailsControllerRef.current?.abort();
        const controller = new AbortController();
        detailsControllerRef.current = controller;
        const capturedLifetime = scopeLifetime;
        setDetailTarget({ kind: 'server', id: operation.receiptId });
        setOperationDetails(null);
        setDetailsError('');
        setDetailsLoading(true);
        setDetailsLoadingMore(false);
        try {
            const details = await getDocumentOperationDetails(
                scope,
                operation.receiptId,
                { signal: controller.signal }
            );
            if (controller.signal.aborted
                || detailsControllerRef.current !== controller
                || !isCurrent(capturedLifetime)) return;
            setOperationDetails(normalizeOperationDetails(details));
        } catch (requestError) {
            if (controller.signal.aborted
                || detailsControllerRef.current !== controller
                || !isCurrent(capturedLifetime)
                || requestError?.name === 'AbortError') return;
            setDetailsError(requestError?.message || 'The item details for this action are unavailable.');
        } finally {
            if (detailsControllerRef.current === controller && isCurrent(capturedLifetime)) {
                detailsControllerRef.current = null;
                setDetailsLoading(false);
            }
        }
    };

    const toggleLocalDetails = (group) => {
        if (detailTarget?.kind === 'local' && detailTarget.id === group.id) {
            resetDetails();
            return;
        }
        detailsControllerRef.current?.abort();
        detailsControllerRef.current = null;
        setDetailTarget({ kind: 'local', id: group.id });
        setOperationDetails({
            items: group.items,
            total: group.items.length,
            available: true,
            truncated: false,
        });
        setDetailsLoading(false);
        setDetailsLoadingMore(false);
        setDetailsError('');
    };

    const loadMoreServerDetails = async () => {
        if (detailTarget?.kind !== 'server' || !operationDetails?.truncated
            || detailsLoading || detailsLoadingMore) return;
        detailsControllerRef.current?.abort();
        const controller = new AbortController();
        detailsControllerRef.current = controller;
        const capturedLifetime = scopeLifetime;
        const receiptId = detailTarget.id;
        const offset = operationDetails.items.length;
        setDetailsError('');
        setDetailsLoadingMore(true);
        try {
            const nextPage = normalizeOperationDetails(await getDocumentOperationDetails(
                scope,
                receiptId,
                { signal: controller.signal, offset }
            ));
            if (controller.signal.aborted
                || detailsControllerRef.current !== controller
                || !isCurrent(capturedLifetime)) return;
            setOperationDetails(current => {
                if (!current) return nextPage;
                const items = [...current.items, ...nextPage.items];
                return {
                    items,
                    total: Math.max(current.total, nextPage.total, items.length),
                    available: current.available && nextPage.available,
                    truncated: nextPage.truncated && items.length < nextPage.total,
                };
            });
        } catch (requestError) {
            if (controller.signal.aborted
                || detailsControllerRef.current !== controller
                || !isCurrent(capturedLifetime)
                || requestError?.name === 'AbortError') return;
            setDetailsError(requestError?.message || 'More item details could not be loaded.');
        } finally {
            if (detailsControllerRef.current === controller && isCurrent(capturedLifetime)) {
                detailsControllerRef.current = null;
                setDetailsLoadingMore(false);
            }
        }
    };

    const reviewLocalUploadGroup = (groupId) => {
        close({ restoreFocus: false });
        window.setTimeout(() => onReviewLocalUploadGroup?.(groupId), 0);
    };

    const dismissLocalUploadGroup = (groupId) => {
        if (detailTarget?.kind === 'local' && detailTarget.id === groupId) resetDetails();
        onDismissLocalUploadGroup?.(groupId);
    };

    const renderDetails = (fallbackCount) => {
        const details = operationDetails;
        return (
            <div className="modal-content-wrapper document-recent-actions-detail-modal">
                {detailsLoading && (
                    <div className="document-recent-actions-detail-state small-text" role="status">
                        <Loader2 size={16} aria-hidden="true" />
                        Loading item details…
                    </div>
                )}
                {!detailsLoading && detailsError && (
                    <Alert
                        message={detailsError}
                        type="warning"
                        onClose={() => setDetailsError('')}
                    />
                )}
                {!detailsLoading && details?.items?.length > 0 && (
                    <div
                        className="users-table-wrapper document-recent-actions-items-table-wrap"
                        role="region"
                        aria-label="Files in this action"
                        tabIndex={0}
                    >
                        <table className="data-table default document-recent-actions-items-table">
                            <thead>
                                <tr>
                                    <th>File</th>
                                    <th>Location</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.items.map((item, index) => (
                                    <tr key={`${item.id}-${index}`}>
                                        <td>{item.name}</td>
                                        <td className="document-recent-actions-item-path">{item.path}</td>
                                        <td>{item.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!detailsLoading && !detailsError && details && details.items.length === 0 && (
                    <p className="document-recent-actions-detail-state small-text">
                        {!details.available || fallbackCount > 0
                            ? 'Item details are unavailable for this recorded action.'
                            : 'No items were recorded for this action.'}
                    </p>
                )}
                {!detailsLoading && !detailsError && details?.truncated && (
                    <div className="document-recent-actions-detail-pagination">
                        <p className="document-recent-actions-detail-note small-text">
                            Showing {details.items.length.toLocaleString()} of {details.total.toLocaleString()} items.
                        </p>
                        <button
                            type="button"
                            className="action alt small-text"
                            disabled={detailsLoadingMore}
                            onClick={loadMoreServerDetails}
                        >
                            {detailsLoadingMore ? 'Loading…' : 'Load more'}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const selectedLocalGroup = detailTarget?.kind === 'local'
        ? normalizedLocalUploadGroups.find(group => group.id === detailTarget.id)
        : null;
    const selectedOperation = detailTarget?.kind === 'server'
        ? operations.find(operation => operation.receiptId === detailTarget.id)
        : null;
    const selectedObjectCount = selectedLocalGroup?.objectCount
        ?? selectedOperation?.objectCount
        ?? 0;
    const selectedOperationLabel = selectedLocalGroup
        ? 'Upload'
        : (OPERATION_LABELS[selectedOperation?.operation] || 'Document action');

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="action quaternary small-text document-toolbar-icon-button document-recent-actions-trigger"
                aria-label={`Recent document actions${actionCount > 0
                    ? `, ${actionCount} recorded`
                    : ''}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Recent document actions"
                disabled={!validScope}
                onClick={() => {
                    setOpen(true);
                    // Opening always revalidates current server state. The
                    // mount-time load is only a badge hint and may be stale or
                    // may have been interrupted in another tab.
                    load();
                    loadCachedCaseZip();
                }}
            >
                <History size={18} aria-hidden="true" />
                <span className="document-recent-actions-trigger-label">Recent actions</span>
                {actionCount > 0 && (
                    <span className="document-recent-actions-count" aria-hidden="true">
                        {Math.min(actionCount, 99)}
                    </span>
                )}
            </button>
            {open && !detailTarget && createPortal(
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Recent document actions"
                    onKeyDown={handleDialogKeyDown}
                >
                    <Modal
                        title="Recent document actions"
                        instructions="Your recent changes in this case."
                        onClose={close}
                        footer={(
                            <div className="modal-footer-actions">
                                <button
                                    type="button"
                                    className="action alt"
                                    onClick={() => {
                                        load();
                                        loadCachedCaseZip();
                                    }}
                                    disabled={loading || cachedCaseZipLoading || Boolean(busyReceiptId)}
                                >
                                    Refresh
                                </button>
                                <button type="button" className="action" onClick={close}>Close</button>
                            </div>
                        )}
                    >
                        <div className="modal-content-wrapper document-recent-actions-modal">
                            {error && <Alert message={error} type="warning" onClose={() => setError('')} />}
                            {loading && (
                                <div className="document-recent-actions-state small-text" role="status">
                                    <Loader2 size={18} aria-hidden="true" />
                                    Loading recent actions…
                                </div>
                            )}
                            {!loading && !error && actionCount === 0 && (
                                <div className="document-recent-actions-state small-text">
                                    <History size={22} aria-hidden="true" />
                                    No recent document actions for this case.
                                </div>
                            )}
                            {actionCount > 0 && (
                                <div className="document-recent-actions-list">
                                    {caseZipVisible && (
                                        <div className="document-recent-actions-entry">
                                            <div className="document-recent-actions-row">
                                                <div className="document-recent-actions-details">
                                                    <div className="document-recent-actions-heading">
                                                        <strong>Case ZIP</strong>
                                                        <span className={`document-recent-actions-status is-${cachedCaseZip.status}`}>
                                                            {cachedCaseZip.status === 'ready' ? 'Ready' : 'In progress'}
                                                        </span>
                                                    </div>
                                                    <span className="small-text">
                                                        {cachedCaseZip.status === 'ready'
                                                            ? `${fileSizeLabel(cachedCaseZip.sizeBytes)} · Created ${operationTime(cachedCaseZip.createdAt)}`
                                                            : `${cachedCaseZip.message} · ${cachedCaseZip.progress}%`}
                                                    </span>
                                                    {cachedCaseZip.status === 'building' && (
                                                        <div
                                                            className="progress-bar"
                                                            role="progressbar"
                                                            aria-label="Case ZIP progress"
                                                            aria-valuemin="0"
                                                            aria-valuemax="100"
                                                            aria-valuenow={cachedCaseZip.progress}
                                                        >
                                                            <div
                                                                className="progress-fill"
                                                                style={{ width: `${cachedCaseZip.progress}%` }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="document-recent-actions-row-actions">
                                                    {cachedCaseZip.status === 'ready' ? (
                                                        <a
                                                            className="action quaternary small-text"
                                                            href={cachedCaseZip.downloadUrl}
                                                            download
                                                            aria-label="Download Case ZIP"
                                                            title="Download Case ZIP"
                                                        >
                                                            <Download size={16} aria-hidden="true" />
                                                        </a>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="action quaternary small-text"
                                                            disabled
                                                            aria-label="Download Case ZIP"
                                                            title="Case ZIP is still being created"
                                                        >
                                                            <Download size={16} aria-hidden="true" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {normalizedLocalUploadGroups.map(group => {
                                        return (
                                            <div className="document-recent-actions-entry" key={`local-${group.id}`}>
                                                <div className="document-recent-actions-row">
                                                    <div className="document-recent-actions-details">
                                                        <div className="document-recent-actions-heading">
                                                            <strong>Upload</strong>
                                                            <span className={`document-recent-actions-status is-${group.status}`}>
                                                                {STATUS_LABELS[group.status]}
                                                            </span>
                                                        </div>
                                                        <span className="small-text">
                                                            {group.objectCount.toLocaleString()} {group.objectCount === 1 ? 'item' : 'items'} · {group.destinationLabel}
                                                        </span>
                                                    </div>
                                                    <div className="document-recent-actions-row-actions">
                                                        <button
                                                            type="button"
                                                            className="action alt small-text"
                                                            onClick={() => toggleLocalDetails(group)}
                                                        >
                                                            Details
                                                        </button>
                                                        {group.canReview && typeof onReviewLocalUploadGroup === 'function' && (
                                                            <button
                                                                type="button"
                                                                className="action small-text"
                                                                onClick={() => reviewLocalUploadGroup(group.id)}
                                                            >
                                                                Review
                                                            </button>
                                                        )}
                                                        {typeof onDismissLocalUploadGroup === 'function' && (
                                                            <button
                                                                type="button"
                                                                className="action quaternary small-text"
                                                                onClick={() => dismissLocalUploadGroup(group.id)}
                                                            >
                                                                Dismiss
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {operations.map(operation => (
                                        <div className="document-recent-actions-entry" key={operation.receiptId}>
                                            <div className="document-recent-actions-row">
                                                <div className="document-recent-actions-details">
                                                    <div className="document-recent-actions-heading">
                                                        <strong>{OPERATION_LABELS[operation.operation]}</strong>
                                                        <span className={`document-recent-actions-status is-${operation.status}`}>
                                                            {operation.cleanupPending
                                                                ? 'Finishing cleanup'
                                                                : (STATUS_LABELS[operation.status] || 'Recorded')}
                                                        </span>
                                                    </div>
                                                    <span className="small-text">
                                                        {operation.objectCount.toLocaleString()} {operation.objectCount === 1 ? 'item' : 'items'} · {operationTime(operation.createdAt)}
                                                    </span>
                                                </div>
                                                <div className="document-recent-actions-row-actions">
                                                    <button
                                                        type="button"
                                                        className="action alt small-text"
                                                        onClick={() => toggleServerDetails(operation)}
                                                    >
                                                        Details
                                                    </button>
                                                    {operation.canUndo && (
                                                        <button
                                                            type="button"
                                                            className="action small-text"
                                                            disabled={Boolean(busyReceiptId) || loading}
                                                            onClick={() => undo(operation)}
                                                        >
                                                            {busyReceiptId === operation.receiptId ? 'Undoing…' : 'Undo'}
                                                        </button>
                                                    )}
                                                    {!operation.canUndo && operation.canResume && (
                                                        <button
                                                            type="button"
                                                            className="action small-text"
                                                            disabled={Boolean(busyReceiptId) || loading}
                                                            onClick={() => resume(operation)}
                                                        >
                                                            {busyReceiptId === operation.receiptId
                                                                ? 'Resuming…'
                                                                : operation.resumeLabel}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Modal>
                </div>,
                document.body
            )}
            {open && detailTarget && (selectedLocalGroup || selectedOperation) && createPortal(
                <div
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${selectedOperationLabel} item details`}
                    onKeyDown={handleDialogKeyDown}
                >
                    <Modal
                        wide
                        title={`${selectedOperationLabel} details`}
                        instructions={`${selectedObjectCount.toLocaleString()} ${selectedObjectCount === 1 ? 'item' : 'items'} in this action.`}
                        onClose={close}
                        footer={(
                            <div className="modal-footer-actions">
                                <button
                                    type="button"
                                    className="action alt"
                                    onClick={resetDetails}
                                >
                                    Back to recent actions
                                </button>
                                <button type="button" className="action" onClick={close}>Close</button>
                            </div>
                        )}
                    >
                        {renderDetails(selectedObjectCount)}
                    </Modal>
                </div>,
                document.body
            )}
        </>
    );
};

export default DocumentRecentActions;
